import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Tooltip,
  IconButton,
  Typography
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { InfoOutlined as InfoIcon } from "@material-ui/icons";
import ConfigurationService from "../../../services/ConfigurationService";

const AppSettings = () => {
  const [defaultPeriod, setDefaultPeriod] = useState("");
  const [initialDefaultPeriod, setInitialDefaultPeriod] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const loadAppDefaultPeriod = useCallback(async () => {
    setLoading(true);
    setFeedback(null);

    try {
      const response = await ConfigurationService.getAppDefaultPeriod();
      const value = response && response.value ? response.value : "";
      const normalizedValue = value ? value.toUpperCase() : "";

      setDefaultPeriod(normalizedValue);
      setInitialDefaultPeriod(normalizedValue);
    } catch (error) {
      console.error("Erro ao carregar período padrão do app:", error);
      setFeedback({
        type: "error",
        message:
          "Não foi possível carregar o período padrão. Tente novamente mais tarde."
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAppDefaultPeriod();
  }, [loadAppDefaultPeriod]);

  const handlePeriodChange = event => {
    setDefaultPeriod(event.target.value.toUpperCase());
    setFeedback(null);
  };

  const saveAppDefaultPeriod = async () => {
    const trimmedValue = defaultPeriod ? defaultPeriod.trim() : "";

    setSaving(true);
    setFeedback(null);

    try {
      const response = await ConfigurationService.saveAppDefaultPeriod({
        value: trimmedValue
      });

      const savedValue = response && response.value ? response.value : "";
      const normalizedSavedValue = savedValue ? savedValue.toUpperCase() : "";

      setDefaultPeriod(normalizedSavedValue);
      setInitialDefaultPeriod(normalizedSavedValue);
      setFeedback({
        type: "success",
        message: "Período padrão atualizado com sucesso."
      });
    } catch (error) {
      console.error("Erro ao salvar período padrão do app:", error);
      setFeedback({
        type: "error",
        message: "Não foi possível salvar o período padrão. Tente novamente."
      });
    } finally {
      setSaving(false);
    }
  };

  const trimmedCurrent = defaultPeriod ? defaultPeriod.trim() : "";
  const trimmedInitial = initialDefaultPeriod
    ? initialDefaultPeriod.trim()
    : "";
  const hasChanged = trimmedCurrent !== trimmedInitial;
  const isSavingDisabled = saving || loading || !hasChanged;

  return (
    <Box p={3}>
      {feedback && (
        <Box mb={2}>
          <Alert severity={feedback.type}>{feedback.message}</Alert>
        </Box>
      )}
      <Box display="flex" flexDirection="column">
        <Box display="flex" alignItems="center" mb={1}>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle1" style={{ marginBottom: 0 }}>
              Período padrão do boletim
            </Typography>
            <Tooltip
              title="Quando o usuário abre o boletim, o app primeiro tenta usar o último período que ele escolheu. Se não houver, aplica o código definido aqui. Sem código, o app mostra o primeiro período disponível da lista."
              placement="right"
            >
              <IconButton
                aria-label="Informações sobre período padrão do boletim"
                size="small"
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Digite o código do período que deve aparecer por padrão no boletim
          (ex.: 1TRI, 2BI, FINAL). Deixe vazio para manter o primeiro período
          disponível.
        </Typography>
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <TextField
            label="Código do período"
            variant="outlined"
            placeholder="Ex.: 1TRI"
            value={defaultPeriod}
            onChange={handlePeriodChange}
            disabled={loading || saving}
            inputProps={{ maxLength: 20 }}
          />
          <Box ml={{ xs: 0, sm: 2 }} mt={{ xs: 2, sm: 0 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={saveAppDefaultPeriod}
              disabled={isSavingDisabled}
            >
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AppSettings;
